import { faArrowLeft, faArrowRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { Button, IButtonProps } from '../../flayr-components/Button/Button';
import { Flex } from '../../flayr-components/Flex/Flex';
import { ShowIf } from '../../flayr-components/ShowIf/ShowIf';
import ArrayMap from '../ArrayMap/ArrayMap';

interface IPaginationProps {
  total?: number;
  page?: number;
  limit?: number;
  currentRecordsCount?: number;
  className?: string;
  type?: 'default' | 'loadMore';
  loadMoreLabel?: string;
  loadMoreBtnProps?: IButtonProps;
  onGotoPage?: (e?: any) => void;
  onLoadMore?: (e?: any) => void;
}

const Pagination: React.FC<React.PropsWithChildren<IPaginationProps>> = ({
  total = 0,
  page = 1,
  limit = 10,
  currentRecordsCount,
  className,
  type = 'default',
  onGotoPage,
  onLoadMore,
  loadMoreLabel = 'Load More',
  loadMoreBtnProps = { size: 'lg', color: 'primary' },
}) => {
  function handleGoTopage(e, page) {
    onGotoPage?.(page);
  }

  function handleNextOrPrev(e, id) {
    if (id === 'next') {
      onGotoPage?.(page + 1);
    } else {
      onGotoPage?.(page - 1);
    }
  }

  function handleLoadMore() {
    onLoadMore?.((page + 1) * limit);
  }

  const totalPages = Math.ceil(+total / +(limit || 1));

  return (
    <ShowIf
      condition={
        total &&
        currentRecordsCount &&
        total > currentRecordsCount &&
        totalPages
      }
    >
      <div className={className}>
        <ShowIf
          condition={type === 'default'}
          elseTemplate={
            <Flex center className="w-full">
              <Button {...loadMoreBtnProps} onClick={handleLoadMore}>
                {loadMoreLabel}
              </Button>
            </Flex>
          }
        >
          {/* mobile */}
          <Flex className="md:hidden">
            <ShowIf condition={page > 1}>
              <div className="mr-4">
                <Button size="lg" onClick={onGotoPage} id="1">
                  <FontAwesomeIcon icon={faArrowLeft} />

                  <span className="invisible -ml-3">a</span>
                </Button>
              </div>
            </ShowIf>
            <div>
              <Button
                color="primary"
                size="lg"
                disabled={page === 1}
                id="prev"
                onClick={handleNextOrPrev}
              >
                <FontAwesomeIcon icon={faArrowLeft} />
                <span className="invisible -ml-3">a</span>
              </Button>
            </div>
            <div className="flex-grow px-4 font-medium text-center md:hidden">
              {page} of {totalPages}
            </div>
            <div>
              <Button
                color="primary"
                size="lg"
                disabled={page === totalPages}
                id="next"
                onClick={handleNextOrPrev}
              >
                <FontAwesomeIcon icon={faArrowRight} />
                <span className="invisible -ml-3">a</span>
              </Button>
            </div>
          </Flex>

          {/* desktop */}
          <Flex className="hidden w-full p-2 bg-white border rounded shadow-md md:flex">
            <div className="mr-2">
              <Button
                color="primary"
                clear
                size="lg"
                disabled={page === 1}
                id="prev"
                onClick={handleNextOrPrev}
              >
                <FontAwesomeIcon icon={faArrowLeft} />
                <span className="invisible -ml-3">a</span>
              </Button>
            </div>
            <Flex>
              <ArrayMap
                items={Array(+totalPages || 0).fill(totalPages)}
                render={(_x, num) => (
                  <div className="mx-2">
                    <Button
                      outline
                      disabled={num + 1 === page}
                      id={num + 1}
                      onClick={handleGoTopage}
                    >
                      {num + 1}
                    </Button>
                  </div>
                )}
              />
            </Flex>
            <div className="mr-3">
              <Button
                color="primary"
                clear
                disabled={page === totalPages}
                id="next"
                onClick={handleNextOrPrev}
              >
                <FontAwesomeIcon icon={faArrowRight} />
                <span className="invisible -ml-3">a</span>
              </Button>
            </div>
            <div className="pr-4 ml-auto">
              showing {page} - {totalPages}
            </div>
          </Flex>
        </ShowIf>
      </div>
    </ShowIf>
  );
};
export default Pagination;
